import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import { sceneToFlow, isMindScene, resolveEnterTarget } from "./sceneToFlow.js";
import { nodeTypes } from "./nodes.jsx";
import { applyPose, commitNodePose, foldPositionChanges, idKeyOf, paintNodes } from "./pose.js";

const DEFAULT_OPTS = { boundary: true, internal: false, power: false, ports: true };
const EMPTY_LIST = [];

function enterPayload(node) {
  const pageId = node?.data?.enterTarget || resolveEnterTarget(node?.data);
  if (!pageId) return null;
  return { pageId, instanceId: pageId !== node.id ? node.id : null };
}

function FlowInner({
  scene,
  pageMode,
  focusSheet,
  opts,
  nameMode,
  filter,
  selection,
  highlight,
  onSelect,
  onEnterSheet,
  onSelectionContextMenu,
  onNodesChange: onNodesChangeProp,
  fitToken,
  nodesDraggable = true,
}) {
  const built = useMemo(() => {
    if (!isMindScene(scene)) return { nodes: [], edges: [], pages: [] };
    return sceneToFlow(scene, opts || DEFAULT_OPTS, filter, nameMode, pageMode, focusSheet);
  }, [scene, opts, filter, nameMode, pageMode, focusSheet]);

  const enterRef = useRef(onEnterSheet);
  enterRef.current = onEnterSheet;

  const fireEnter = useCallback((node) => {
    const target = enterPayload(node);
    if (!target) return;
    enterRef.current?.(target);
  }, []);

  const sel = selection?.length ? selection : EMPTY_LIST;
  const hi = highlight?.length ? highlight : EMPTY_LIST;
  const painted = useMemo(
    () => paintNodes(built.nodes, sel, hi, fireEnter),
    [built.nodes, sel, hi, fireEnter],
  );

  const [pose, setPose] = useState(() => new Map());
  const idKey = useMemo(() => idKeyOf(built.nodes), [built.nodes]);

  useEffect(() => {
    setPose(new Map());
  }, [pageMode, focusSheet, idKey]);

  const nodes = useMemo(() => applyPose(painted, pose), [painted, pose]);

  const onNodesChange = useCallback(
    (changes) => {
      setPose((prev) => foldPositionChanges(prev, changes));
      onNodesChangeProp?.(changes);
    },
    [onNodesChangeProp],
  );

  const onNodeDragStop = useCallback((_, node) => {
    setPose((prev) => commitNodePose(prev, node.id, node.position));
  }, []);

  const { fitView } = useReactFlow();
  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;

  useEffect(() => {
    if (!painted.length) return;
    const t = setTimeout(() => fitViewRef.current({ padding: 0.14, duration: 220 }), 70);
    return () => clearTimeout(t);
  }, [fitToken, painted.length, pageMode, focusSheet]);

  const onNodeClick = useCallback((_, node) => onSelect?.(node.data), [onSelect]);
  const onNodeDoubleClick = useCallback(
    (_, node) => {
      if (node.data?.canEnter) fireEnter(node);
    },
    [fireEnter],
  );
  const onNodeContextMenu = useCallback(
    (ev, node) => {
      if (!onSelectionContextMenu) return;
      ev.preventDefault();
      onSelectionContextMenu(node.data, ev);
    },
    [onSelectionContextMenu],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) {
        return;
      }
      const sel = selection || EMPTY_LIST;
      const node =
        nodes.find((n) => sel.includes(n.id) && n.data?.canEnter) ||
        nodes.find((n) => n.selected && n.data?.canEnter);
      if (!node) return;
      e.preventDefault();
      fireEnter(node);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nodes, selection, fireEnter]);

  if (!isMindScene(scene)) return <div className="empty pad">Load a scene…</div>;

  return (
    <ReactFlow
      nodes={nodes}
      edges={built.edges}
      nodeTypes={nodeTypes}
      onNodeClick={onNodeClick}
      onNodeDoubleClick={onNodeDoubleClick}
      onNodeContextMenu={onNodeContextMenu}
      onNodesChange={onNodesChange}
      onNodeDragStop={onNodeDragStop}
      nodesDraggable={nodesDraggable}
      nodesConnectable={false}
      minZoom={0.1}
      maxZoom={2.5}
      proOptions={{ hideAttribution: true }}
      defaultEdgeOptions={{ type: "smoothstep" }}
    >
      <Background gap={20} size={1} color="#1e293b" />
      <Controls showInteractive={false} />
      <MiniMap
        nodeColor={(n) => n.data?.color || "#334155"}
        maskColor="rgba(2,6,23,.7)"
        style={{ background: "#0f172a" }}
      />
    </ReactFlow>
  );
}

/**
 * Controlled mind-scene/1.1 projection. Does not write node poses back to the IR.
 * Same shape as React Flow: fill a sized parent, pass data + callbacks.
 * Hosts import `@rscircuit/mind-canvas/styles.css` and `@xyflow/react/dist/style.css`.
 */
export function MindCanvas({ className, style, nodesDraggable = true, ...props }) {
  return (
    <div className={className ? `canvas-host ${className}` : "canvas-host"} style={style}>
      <ReactFlowProvider>
        <FlowInner nodesDraggable={nodesDraggable} {...props} />
      </ReactFlowProvider>
    </div>
  );
}
